import { searchState as searchStateUtil } from 'utils';
import equal from 'fast-deep-equal';

const mergeValues = (type, newValue, ...values) => {
    if (type === 'checkbox') {
        const newArrayValues = new Set(...values); // use set to dedupe values if needed
        newArrayValues.add(newValue);
        return newArrayValues.size === 1
            ? Array.from(newArrayValues)[0]
            : [...newArrayValues];
    }
    return newValue;
};

const getValues = (context, key, defaultValue) =>
    (!!context &&
        !!context[key] &&
        (Array.isArray(context[key]) ? context[key] : [context[key]])) ||
    defaultValue;

const getAddFacetKey = (operator, type) =>
    `add${
        operator === 'and' && type !== 'range' ? 'Facet' : 'DisjunctiveFacet'
    }`;

const getRemoveFacetKey = (operator, type) =>
    `remove${
        operator === 'and' && type !== 'range' ? 'Facet' : 'DisjunctiveFacet'
    }`;

const getFacetRefinementKeyByOperator = operator =>
    `${operator === 'or' ? 'disjunctiveFacets' : 'facets'}Refinements`;

export const isFacetRefinementMissing = (
    searchParameters,
    searchState,
    attributes,
) =>
    attributes.some(({ key, type, operator }) => {
        const searchStateNamespace = searchStateUtil.getSearchStateNamespaceFromFilterType(
            type,
        );
        const facetRefinementsKey = getFacetRefinementKeyByOperator(operator);
        const currentValue =
            searchParameters[facetRefinementsKey] &&
            searchParameters[facetRefinementsKey][key]
                ? searchParameters[facetRefinementsKey][key]
                : undefined;
        const value =
            searchState[searchStateNamespace] &&
            searchState[searchStateNamespace][key]
                ? searchState[searchStateNamespace][key]
                : undefined;

        if (type === 'range') {
            return !equal(currentValue, value);
        }

        if (Array.isArray(currentValue) && Array.isArray(value)) {
            return currentValue.some(val => !value.includes(val));
        }

        return Array.isArray(currentValue)
            ? !currentValue.includes(value)
            : !equal(currentValue, value);
    });

const getClearedValueFromArray = (currentValue, valueToRemove) => {
    let clearedValue = null;
    if (typeof valueToRemove === 'string') {
        clearedValue = currentValue.filter(value => value !== valueToRemove);
        if (clearedValue.length === 1) {
            clearedValue = clearedValue[0];
        }
    } else if (Array.isArray(valueToRemove)) {
        clearedValue = currentValue.filter(
            value => !valueToRemove.includes(value),
        );
    }

    return clearedValue;
};

const getClearedSearchStateValue = (currentValue, valueToRemove) => {
    let clearedValue;

    if (currentValue === valueToRemove) {
        clearedValue = null;
    } else if (Array.isArray(currentValue)) {
        clearedValue = getClearedValueFromArray(currentValue, valueToRemove);
    } else if (
        (Array.isArray(valueToRemove) &&
            valueToRemove.includes(currentValue)) ||
        (typeof valueToRemove === 'object' && typeof currentValue === 'object')
    ) {
        clearedValue = null;
    }
    return clearedValue;
};

const getResetedNamespaces = searchState =>
    Object.keys(searchState)
        .filter(namespace => searchStateUtil.NAMESPACES.includes(namespace))
        .reduce(
            (result, namespace) => ({
                ...result,
                [namespace]: null,
            }),
            {},
        );

const getClearedNamespaceFromClearRefinements = (namespace, clearRefinements) =>
    Object.keys(namespace).reduce((filters, filterKey) => {
        const currentClearRefinement = clearRefinements.find(
            clearRefinement => clearRefinement.key === filterKey,
        );
        if (currentClearRefinement) {
            return {
                ...filters,
                [filterKey]: getClearedSearchStateValue(
                    namespace[filterKey],
                    currentClearRefinement.value,
                ),
            };
        }
        return filters;
    }, namespace);

const getClearedSearchStateFromClearRefinements = (
    searchState,
    clearRefinements,
) => {
    return Object.keys(searchState)
        .filter(namespace => searchStateUtil.NAMESPACES.includes(namespace))
        .reduce(
            (result, namespace) => ({
                ...result,
                [namespace]: getClearedNamespaceFromClearRefinements(
                    searchState[namespace],
                    clearRefinements,
                ),
            }),
            {},
        );
};

export const getClearedSearchState = (
    searchState,
    { replaceSearchState, clearRefinements },
) => {
    let clearedSearchState = { ...searchState };
    if (replaceSearchState) {
        clearedSearchState = getResetedNamespaces(clearedSearchState);
    } else if (clearRefinements) {
        clearedSearchState = getClearedSearchStateFromClearRefinements(
            clearedSearchState,
            clearRefinements,
        );
    }
    return clearedSearchState;
};

const getDefaultValueFromType = type => {
    if (type === 'range') {
        return {};
    }
    if (['bool', 'checkbox'].includes(type)) {
        return undefined;
    }
    return [];
};

export const addAttributesToSearchParameters = (
    searchParameters,
    attributes,
) => {
    let nextSearchParameters = searchParameters;
    attributes.forEach(({ key, operator, type }) => {
        if (typeof key === 'number' || typeof key === 'string') {
            const addKey = getAddFacetKey(operator, type);
            nextSearchParameters = nextSearchParameters[addKey](key);
        }
    });
    return nextSearchParameters;
};

const getSearchParametersValuesToRemove = (
    namespaceSearchState,
    searchParametersFacetRefinements,
    attribute,
) =>
    searchParametersFacetRefinements &&
    searchParametersFacetRefinements[attribute]
        ? searchParametersFacetRefinements[attribute].filter(currentValue => {
              if (Array.isArray(namespaceSearchState[attribute])) {
                  return !namespaceSearchState[attribute].includes(
                      currentValue,
                  );
              }
              return namespaceSearchState[attribute] !== currentValue;
          })
        : [];

const removeSearchParametersValues = (
    searchParameters,
    valuesToRemove,
    removeRefinementKey,
    attribute,
) => {
    let nextSearchParameters = searchParameters;
    valuesToRemove.forEach(currentValueToRemove => {
        nextSearchParameters = nextSearchParameters[removeRefinementKey](
            attribute,
            currentValueToRemove,
        );
    });
    return nextSearchParameters;
};
export const updateRefinementsToSearchParameters = (
    searchParameters,
    searchState,
    attributes,
) => {
    let nextSearchParameters = searchParameters;
    attributes.forEach(({ key, type, operator }) => {
        const namespace = searchStateUtil.getSearchStateNamespaceFromFilterType(
            type,
        );
        const value = searchState[namespace] && searchState[namespace][key];
        if (type === 'range') {
            if (typeof value === 'object') {
                if (value.min !== undefined) {
                    nextSearchParameters = nextSearchParameters.addNumericRefinement(
                        key,
                        '>=',
                        value.min,
                    );
                }
                if (value.max !== undefined) {
                    nextSearchParameters = nextSearchParameters.addNumericRefinement(
                        key,
                        '<=',
                        value.max,
                    );
                }
            }
        } else {
            const facetRefinementsKey = getFacetRefinementKeyByOperator(
                operator,
            );
            const removeKey = getRemoveFacetKey(operator);
            const valuesToRemove = getSearchParametersValuesToRemove(
                searchState[namespace],
                nextSearchParameters[facetRefinementsKey],
                key,
            );

            nextSearchParameters = removeSearchParametersValues(
                nextSearchParameters,
                valuesToRemove,
                `${removeKey}Refinement`,
                key,
            );

            const addKey = `${getAddFacetKey(operator)}Refinement`;
            if (Array.isArray(value)) {
                value.forEach(currentValue => {
                    if (currentValue) {
                        nextSearchParameters = nextSearchParameters[addKey](
                            key,
                            currentValue,
                        );
                    }
                });
            } else if (value) {
                nextSearchParameters = nextSearchParameters[addKey](key, value);
            }
        }
    });
    return nextSearchParameters;
};

export const getRefinedSearchState = (props, searchState, nextRefinements) =>
    nextRefinements.reduce((searchStateUpdate, { key, value, type }) => {
        const searchStateNamespace = searchStateUtil.getSearchStateNamespaceFromFilterType(
            type,
        );

        let newValue = value;
        if (!props.replaceAttributeValues) {
            // default value for baseValue and updateValues
            const defaultValue = getDefaultValueFromType(type);
            // state before applying multiple filters
            const baseValues = getValues(
                searchState[searchStateNamespace],
                key,
                defaultValue,
            );
            // updating state
            const updateValues = getValues(
                searchStateUpdate[searchStateNamespace],
                key,
                defaultValue,
            );

            newValue = mergeValues(type, value, baseValues, updateValues);
        }

        return {
            ...searchStateUpdate,
            [searchStateNamespace]: {
                ...(props.replaceSearchState
                    ? {}
                    : searchStateUpdate[searchStateNamespace]),
                [key]: newValue,
            },
        };
    }, searchState);

export const getCurrentRefinements = (searchState, attributes) =>
    attributes.reduce((currentRefinements, { key, type }) => {
        const searchStateNamespace = searchStateUtil.getSearchStateNamespaceFromFilterType(
            type,
        );
        const values = (searchState[searchStateNamespace] || {})[key];
        return {
            ...currentRefinements,
            [key]: values,
        };
    }, {});

export const areMissingAttributesInSearchParameters = (
    searchParameters,
    attributes,
) =>
    !attributes.every(attribute =>
        attribute.operator === 'or'
            ? searchParameters.disjunctiveFacets.includes(attribute.key)
            : searchParameters.facets.includes(attribute.key),
    );
